import { useEffect, useRef, useState } from 'react';
import { fetchItems } from 'utils/api/categories.api';
import { reduceIdsWithSelectedFields } from './utils';

export function useGetWatchlistHashtagIds(watchlistItems) {
    const [hashtagCategoryIds, setHashtagCategoryIds] = useState([]);
    const [hashtagBrandIds, setHashtagBrandIds] = useState([]);
    const [relatedHashtagCategoryIds, setRelatedHashtagCategoryIds] = useState(
        [],
    );
    const [relatedHashtagBrandIds, setRelatedHashtagBrandIds] = useState([]);
    const [relatedHashtagCategoryBrandIds, setRelatedHashtagCategoryBrandIds] =
        useState([]);
    const prevWatchlistItems = useRef([]);

    useEffect(() => {
        const fetchHashtagIds = async () => {
            const categoryItems = reduceIdsWithSelectedFields(
                watchlistItems,
                'hashtag',
                ({ type }) => type === 'hashtag',
                ['name', 'categoryId'],
            );
            const brandItems = reduceIdsWithSelectedFields(
                watchlistItems,
                'hashtag',
                ({ type }) => type === 'hashtag',
                ['name', 'brandId'],
            );

            const relatedCategoryItems = reduceIdsWithSelectedFields(
                watchlistItems,
                'related-hashtag',
                ({ type, brandId }) => type === 'related-hashtag' && !brandId,
                ['name', 'categoryId'],
            );
            const relatedBrandItems = reduceIdsWithSelectedFields(
                watchlistItems,
                'related-hashtag',
                ({ type, categoryId }) =>
                    type === 'related-hashtag' && !categoryId,
                ['name', 'brandId'],
            );

            const relatedCategoryBrandItems = reduceIdsWithSelectedFields(
                watchlistItems,
                'related-hashtag',
                ({ type, categoryId, brandId }) =>
                    type === 'related-hashtag' && categoryId && brandId,
                ['name', 'brandId', 'categoryId'],
            );

            const uniqueNames = Array.from(
                new Set([...categoryItems, ...brandItems].map((i) => i.name)),
            );

            const relatedUniqueNames = Array.from(
                new Set(
                    [
                        ...relatedCategoryItems,
                        ...relatedBrandItems,
                        ...relatedCategoryBrandItems,
                    ].map((i) => i.name),
                ),
            );

            if (uniqueNames.length === 0) {
                setHashtagBrandIds([]);
                setHashtagCategoryIds([]);
            }

            if (relatedUniqueNames.length === 0) {
                setRelatedHashtagBrandIds([]);
                setRelatedHashtagCategoryIds([]);
                setRelatedHashtagCategoryBrandIds([]);
            }

            if (uniqueNames.length === 0 && relatedUniqueNames.length === 0) {
                return;
            }

            try {
                const hashtagsQuery = await fetchItems({
                    domain: 'hashtags',
                    data: {
                        names: uniqueNames.concat(relatedUniqueNames),
                        page: 1,
                    },
                    paginateBy: 'names',
                });

                if (!hashtagsQuery.results?.length) {
                    setHashtagBrandIds([]);
                    setHashtagCategoryIds([]);
                    setRelatedHashtagBrandIds([]);
                    setRelatedHashtagCategoryIds([]);
                    setRelatedHashtagCategoryBrandIds([]);
                    return;
                }

                const hashtagIdMap = Object.fromEntries(
                    hashtagsQuery.results
                        .filter(({ hashtag }) => uniqueNames.includes(hashtag))
                        .map(({ id, hashtag }) => [hashtag, id]),
                );
                const relatedHashtagIdMap = Object.fromEntries(
                    hashtagsQuery.results
                        .filter(({ hashtag }) =>
                            relatedUniqueNames.includes(hashtag),
                        )
                        .map(({ id, hashtag }) => [hashtag, id]),
                );

                categoryItems.forEach((item) => {
                    if (hashtagIdMap[item.name])
                        item.id = hashtagIdMap[item.name];
                });
                brandItems.forEach((item) => {
                    if (hashtagIdMap[item.name])
                        item.id = hashtagIdMap[item.name];
                });
                relatedCategoryItems.forEach((item) => {
                    if (relatedHashtagIdMap[item.name])
                        item.id = relatedHashtagIdMap[item.name];
                });
                relatedBrandItems.forEach((item) => {
                    if (relatedHashtagIdMap[item.name])
                        item.id = relatedHashtagIdMap[item.name];
                });
                relatedCategoryBrandItems.forEach((item) => {
                    if (relatedHashtagIdMap[item.name])
                        item.id = relatedHashtagIdMap[item.name];
                });
                setHashtagCategoryIds(categoryItems);
                setHashtagBrandIds(brandItems);
                setRelatedHashtagCategoryIds(relatedCategoryItems);
                setRelatedHashtagBrandIds(relatedBrandItems);
                setRelatedHashtagCategoryBrandIds(relatedCategoryBrandItems);
            } catch (err) {
                console.error('Failed to fetch hashtags', err);
            }
        };

        const current = watchlistItems || [];
        const prev = prevWatchlistItems.current;
        const wasItemAdded = current.length > prev.length;

        if (current.length && wasItemAdded) {
            fetchHashtagIds();
        } else {
            const deletedItem = prev.find((prevItem) => {
                if (
                    prevItem.type !== 'hashtag' &&
                    prevItem.type !== 'related-hashtag'
                )
                    return false;

                return !current.some((currItem) => {
                    if (currItem.type !== prevItem.type) return false;

                    if (currItem.type === 'hashtag') {
                        return currItem.name === prevItem.name;
                    }

                    if (currItem.type === 'related-hashtag') {
                        const sameName = currItem.name === prevItem.name;

                        const brandMatch =
                            currItem.brandId != null &&
                            prevItem.brandId != null &&
                            currItem.brandId === prevItem.brandId;

                        const categoryMatch =
                            currItem.categoryId != null &&
                            prevItem.categoryId != null &&
                            currItem.categoryId === prevItem.categoryId;

                        return sameName && (brandMatch || categoryMatch);
                    }

                    return false;
                });
            });

            if (deletedItem?.type === 'hashtag') {
                setHashtagCategoryIds((prev) =>
                    prev.filter((item) => item.name !== deletedItem.name),
                );
                setHashtagBrandIds((prev) =>
                    prev.filter((item) => item.name !== deletedItem.name),
                );
            } else if (deletedItem?.type === 'related-hashtag') {
                setRelatedHashtagCategoryIds((prev) =>
                    prev.filter(
                        (item) =>
                            !(
                                item.name === deletedItem.name &&
                                item.categoryId === deletedItem.categoryId
                            ),
                    ),
                );
                setRelatedHashtagBrandIds((prev) =>
                    prev.filter(
                        (item) =>
                            !(
                                item.name === deletedItem.name &&
                                item.brandId === deletedItem.brandId
                            ),
                    ),
                );
                if (deletedItem.brandId && deletedItem.categoryId) {
                    setRelatedHashtagCategoryBrandIds((prev) =>
                        prev.filter(
                            (item) =>
                                !(
                                    item.name === deletedItem.name &&
                                    item.brandId === deletedItem.brandId &&
                                    item.categoryId === deletedItem.categoryId
                                ),
                        ),
                    );
                }
            }
        }

        prevWatchlistItems.current = current;
    }, [watchlistItems]);

    return {
        hashtagCategoryIds,
        hashtagBrandIds,
        relatedHashtagCategoryIds,
        relatedHashtagBrandIds,
        relatedHashtagCategoryBrandIds,
    };
}
